import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { ClickAwayListener, MenuList, Popper, Stack, useTheme, useThemeProps } from "@mui/material";
import { cloneElement, forwardRef, useRef, useState } from "react";
import { useSlotProps } from "../../base/index.js";
import { MoreVert } from "../../icons/index.js";
import { actionBarName, useUtilityClasses } from "./classes.js";
import { useActionBar } from "./hooks/useActionBar.js";
import { useActionItems } from "./hooks/useActionItems.js";
import { List } from "./components/List.js";
import { MenuButton } from "./components/MenuButton.js";
import { MenuTooltip } from "./components/MenuTooltip.js";
import { Root } from "./components/Root.js";
import { TruncateList } from "./components/TruncateList.js";
import { defaultComputeGap } from "./defaultComputeGap.js";
const defaultMenuIcon = /* @__PURE__ */ jsx(MoreVert, { fontSize: "inherit" });
const defaultPopperProps = { placement: "bottom-start" };
const ActionBar = forwardRef(function ActionBar2(props, ref) {
  const themeProps = useThemeProps({ name: actionBarName, props });
  const theme = useTheme();
  const [hiddenCount, setHiddenCount] = useState(0);
  const {
    actions,
    onClick,
    children,
    disabled,
    onChanged,
    components,
    componentsProps,
    size = "medium",
    limit = Number.POSITIVE_INFINITY,
    menuMode = "textIcon",
    listMode = "iconOnly",
    testId = `${actionBarName}-root`,
    menuIcon = defaultMenuIcon,
    popperProps = defaultPopperProps,
    density,
    computeGap = defaultComputeGap,
    menuTooltip,
    ...rest
  } = themeProps;
  const gap = typeof computeGap === "number" ? computeGap : typeof computeGap === "function" ? computeGap(density) : 0;
  const buttonRef = useRef(null);
  const { menuOpen, onOpen, onClose, parentOnClick } = useActionBar({
    actions,
    onClick,
    onChanged,
    buttonRef
  });
  const { buttonItems, menuItems, showMenu, endItem } = useActionItems({
    limit,
    children,
    hiddenCount
  });
  const _internal = {
    size,
    menuMode,
    parentOnClick,
    renderInTruncateList: limit === "auto",
    gap
  };
  const ownerState = {
    ...themeProps,
    size,
    limit,
    menuMode
  };
  const utilityClasses = useUtilityClasses(ownerState);
  const rootSlotProps = useSlotProps({
    ownerState,
    elementType: Root,
    externalForwardedProps: rest,
    className: utilityClasses.root,
    externalSlotProps: componentsProps?.root,
    additionalProps: { ref, as: components?.Root, "data-testid": testId }
  });
  const truncateListSlotProps = useSlotProps({
    ownerState,
    elementType: TruncateList,
    className: utilityClasses.truncateList,
    externalSlotProps: componentsProps?.truncateList,
    additionalProps: {
      as: components?.TruncateList,
      ShowMore: null,
      "data-testid": `${actionBarName}-truncateList`,
      onChangeHiddenItemsCount: setHiddenCount,
      density,
      computeGap
    }
  });
  const listSlotProps = useSlotProps({
    ownerState,
    elementType: List,
    className: utilityClasses.list,
    externalSlotProps: componentsProps?.list,
    additionalProps: { as: components?.List, "data-testid": `${actionBarName}-list` }
  });
  const menuButtonProps = useSlotProps({
    ownerState,
    elementType: MenuButton,
    className: utilityClasses.menuButton,
    externalSlotProps: componentsProps?.menuButton,
    additionalProps: {
      size,
      ref: buttonRef,
      disabled,
      onClick: onOpen,
      as: components?.MenuButton,
      "data-testid": `${actionBarName}-menuBtn`
    }
  });
  const menuTooltipSlotProps = useSlotProps({
    ownerState,
    className: utilityClasses.menuTooltip,
    elementType: MenuTooltip,
    additionalProps: {
      as: components?.MenuTooltip,
      title: menuTooltip ?? ""
    },
    externalSlotProps: componentsProps?.menuTooltip
  });
  const items = buttonItems.map((item, key) => {
    const clonedDisabled = item.props.disabled ?? disabled;
    const clonedProps = {
      key: `${actionBarName}-item-${key}`,
      disabled: clonedDisabled,
      _internal: {
        ..._internal,
        menuMode: listMode
      }
    };
    return cloneElement(item, clonedProps);
  });
  const menuButton = /* @__PURE__ */ jsx(MenuButton, { ...menuButtonProps, children: menuIcon });
  return /* @__PURE__ */ jsxs(Root, { ...rootSlotProps, "data-tidal": "ActionBar", children: [
    limit === "auto" ? /* @__PURE__ */ jsx(TruncateList, { ...truncateListSlotProps, children: items }) : /* @__PURE__ */ jsx(Stack, { gap, direction: "row", children: items }),
    showMenu && /* @__PURE__ */ jsxs(Fragment, { children: [
      menuTooltip ? /* @__PURE__ */ jsx(MenuTooltip, { ...menuTooltipSlotProps, children: menuButton }) : menuButton,
      /* @__PURE__ */ jsx(
        Popper,
        {
          ...popperProps,
          sx: { zIndex: theme.zIndex.modal + 1, ...popperProps?.sx },
          open: menuOpen,
          anchorEl: buttonRef.current,
          children: /* @__PURE__ */ jsx(ClickAwayListener, { onClickAway: onClose, children: /* @__PURE__ */ jsx(List, { ...listSlotProps, children: /* @__PURE__ */ jsx(MenuList, { children: menuItems.map(
            (item, key) => cloneElement(item, {
              key,
              disabled,
              _internal,
              mode: "menu"
            })
          ) }) }) })
        }
      )
    ] }),
    endItem && cloneElement(endItem, {
      _internal: {
        ..._internal,
        menuMode: listMode
      },
      disabled
    })
  ] });
});
export {
  ActionBar
};
//# sourceMappingURL=ActionBar.js.map
